import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from '@/hooks/use-translation'
import * as api from '../api/brand-identity-api'
import type { UpsertBrandIdentityDto } from '../types/brand-identity.types'

export function useGetBrandIdentity() {
  return useQuery({
    queryKey: ['brand-identity'],
    queryFn: api.getBrandIdentity,
  })
}

export function useUpsertBrandIdentity() {
  const queryClient = useQueryClient()
  const t = useTranslation()
  return useMutation({
    mutationFn: (data: UpsertBrandIdentityDto) => api.upsertBrandIdentity(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brand-identity'] })
      toast.success(t('brandIdentity.toast.saveSuccess'))
    },
    onError: (error: Error) => {
      toast.error(t('brandIdentity.toast.saveError', { message: error.message }))
    },
  })
}

export function useUploadBrandLogo() {
  const queryClient = useQueryClient()
  const t = useTranslation()
  return useMutation({
    mutationFn: (file: File) => api.uploadBrandLogo(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brand-identity'] })
      toast.success(t('brandIdentity.toast.logoSuccess'))
    },
    onError: (error: Error) => {
      toast.error(t('brandIdentity.toast.logoError', { message: error.message }))
    },
  })
}

export function useGetReferenceImages() {
  return useQuery({
    queryKey: ['reference-images'],
    queryFn: api.getReferenceImages,
  })
}

export function useUploadReferenceImage() {
  const queryClient = useQueryClient()
  const t = useTranslation()
  return useMutation({
    mutationFn: (file: File) => api.uploadReferenceImage(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reference-images'] })
      toast.success(t('brandIdentity.toast.referenceImageUploadSuccess'))
    },
    onError: (error: Error) => {
      toast.error(t('brandIdentity.toast.referenceImageUploadError', { message: error.message }))
    },
  })
}

export function useDeleteReferenceImage() {
  const queryClient = useQueryClient()
  const t = useTranslation()
  return useMutation({
    mutationFn: (id: string) => api.deleteReferenceImage(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reference-images'] })
      toast.success(t('brandIdentity.toast.referenceImageDeleteSuccess'))
    },
    onError: (error: Error) => {
      toast.error(t('brandIdentity.toast.referenceImageDeleteError', { message: error.message }))
    },
  })
}
